import type { CityConfig } from "../city-template/types";

export const derryConfig: CityConfig = {
  city: "Derry",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "derry",
  route: "/cities/derry",
  title: "Emergency Passport Derry, NH | Fast Expedited Passport Renewal & Same-Day",
  metaDescription: "Emergency passport and expedited passport services in Derry, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Rockingham County. A+ BBB rated, registered State Dept. courier. Serving Londonderry, Salem, Windham.",
  h1: "Emergency & Expedited Passport Services in Derry, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Derry and Rockingham County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Derry%20New%20Hampshire%20downtown%20view%20with%20Broadway%20historic%20district%2C%20charming%20New%20England%20brick%20buildings%20and%20tree-lined%20Main%20Street%2C%20warm%20autumn%20light%20with%20maple%20foliage%2C%20quiet%20suburban%20cityscape%20near%20Manchester%2C%20clean%20and%20inviting%20town%20center%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20soft%20earth%20tones%20and%20warm%20amber%20sky%2C%20photorealistic%20townscape&width=1800&height=700&seq=derry-nh-hero-001&orientation=landscape",
  heroImageAlt: "Derry New Hampshire downtown Broadway historic district with charming New England brick buildings and tree-lined streets",
  geoPosition: "42.8806;-71.3273",
  geoPlacename: "Derry, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Customs House St, Boston, MA 02103",
    distance: "Located in downtown Boston — approximately 45–60 minutes from Derry via I-93",
  },
  nearbyCitiesZips: [
    { name: "Derry", zips: "03038" },
    { name: "Londonderry", zips: "03053" },
    { name: "Salem", zips: "03079" },
    { name: "Windham", zips: "03087" },
    { name: "Hudson", zips: "03051" },
    { name: "Nashua", zips: "03060–03064" },
    { name: "Manchester", zips: "03101–03111" },
    { name: "Merrimack", zips: "03054" },
    { name: "Pelham", zips: "03076" },
    { name: "Atkinson", zips: "03811" },
    { name: "Hampstead", zips: "03841" },
    { name: "Chester", zips: "03036" },
    { name: "Sandown", zips: "03873" },
    { name: "Auburn", zips: "03032" },
    { name: "Hooksett", zips: "03106" },
    { name: "Litchfield", zips: "03052" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Rockingham County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Manchester", route: "/cities/manchester" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Londonderry", route: "/cities/londonderry" },
    { name: "Concord", route: "/cities/concord-nh" },
    { name: "Portsmouth", route: "/cities/portsmouth" },
    { name: "Keene", route: "/cities/keene" },
    { name: "Dover", route: "/cities/dover" },
    { name: "Salem", route: "#" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Derry remotely via secure courier. We do not have a walk-in office in Derry — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Rockingham County travelers.",
  fpcOfficeDistance: "Serving all of Rockingham and Hillsborough Counties",
  serviceAreaIntro: "From Derry's historic Broadway district to the neighborhoods along East Derry Road, from the Londonderry border to the Salem line — Fast Passport Center serves the entire Derry community. We regularly help travelers from Londonderry, Salem, Windham, and Hudson get their passports fast.",
  nearbyCitiesIntro: "We serve all Derry neighborhoods and nearby Rockingham County cities including Londonderry, Salem, Windham, Atkinson, Hampstead, Chester, Sandown, and Pelham. If you are in the greater Derry area, we have you covered.",
  howItWorksIntro: "Most Derry travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Manchester-Boston Regional Airport (MHT) just 15 minutes away and Boston Logan an hour south, Derry travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Derry and Rockingham County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Derry? Here are the most common ones we hear from Rockingham County travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Derry international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Derry", description: "Urgent passport processing for Derry travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal Derry", description: "Fast passport renewal for Derry residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport Application Derry", description: "First-time passport application assistance for Derry residents with step-by-step acceptance clerk guidance." },
    { name: "Lost or Stolen Passport Replacement Derry", description: "Passport replacement service for Derry residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport Service Derry", description: "Passport services for Derry families with minors under 16, including parental consent documentation." },
    { name: "Passport Name Change Derry", description: "Passport name change service for Derry residents after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Amanda R.",
      location: "Derry, NH",
      rating: 5,
      text: "My family cruise to the Bahamas was in 4 days and my passport was expired. Fast Passport Center got me a renewed passport in 36 hours. Saved our vacation from Derry.",
      service: "Emergency Passport",
    },
    {
      name: "Kevin S.",
      location: "Londonderry, NH",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was smooth, they reviewed my documents, and I had my passport in 5 days. Highly recommend from Londonderry.",
      service: "Passport Renewal",
    },
    {
      name: "Michelle D.",
      location: "Salem, NH",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 2 days. Amazing service for Salem residents.",
      service: "Lost Passport",
    },
    {
      name: "Brian T.",
      location: "Windham, NH",
      rating: 5,
      text: "As a frequent international traveler from Windham, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable for Rockingham County.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "derry-1",
      name: "Derry Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 W Broadway",
      city: "Derry",
      state: "NH",
      zip: "03038",
      phone: "(603) 434-2524",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Derry",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Derry. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "derry-2",
      name: "Londonderry Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2 Range Rd",
      city: "Londonderry",
      state: "NH",
      zip: "03053",
      phone: "(603) 432-3282",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.5 miles from Derry",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Derry and Londonderry residents. Popular facility — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "derry-3",
      name: "Salem Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Main St",
      city: "Salem",
      state: "NH",
      zip: "03079",
      phone: "(603) 893-2524",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6.2 miles from Derry",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Full-service acceptance facility. Photo and shipping available. Popular for eastern Rockingham County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "derry-4",
      name: "Windham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4 N Lowell Rd",
      city: "Windham",
      state: "NH",
      zip: "03087",
      phone: "(603) 893-7724",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "5.8 miles from Derry",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Windham and Pelham residents. Limited hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "derry-5",
      name: "Hudson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "Derry Rd",
      city: "Hudson",
      state: "NH",
      zip: "03051",
      phone: "(603) 882-6013",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "8.1 miles from Derry",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Hillsborough County. Limited passport hours — book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "derry-6",
      name: "Nashua Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 Spring St",
      city: "Nashua",
      state: "NH",
      zip: "03060",
      phone: "(603) 882-4012",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12.4 miles from Derry",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for southern New Hampshire. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Derry photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rockingham County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Derry applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Derry families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Derry travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Derry." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Derry address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Derry before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Derry for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Rockingham County travelers." },
    { q: "How fast can I renew my passport in Derry?", a: "Fast Passport Center offers fast passport renewal in Derry in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Derry in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Derry?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Derry residents." },
    { q: "Can I expedite a child passport in Derry?", a: "Yes. Fast Passport Center expedites child passports in Derry for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Derry with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Derry?", a: "With Fast Passport Center's emergency courier service, Derry travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Derry?", a: "Popular options include the Derry Post Office (03038), Londonderry Post Office (03053), Salem Post Office (03079), and Nashua Post Office (03060). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Derry?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Londonderry or Salem?", a: "We serve all of Rockingham County including Londonderry, Salem, Windham, Atkinson, Hampstead, Chester, and Sandown. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Derry?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Derry", address: "58 E Broadway, Derry, NH 03038", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Derry", address: "20 Crystal Ave, Derry, NH 03038", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Londonderry", address: "3 Meetinghouse Rd, Londonderry, NH 03053", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Londonderry", address: "43 Nashua Rd, Londonderry, NH 03053", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Salem", address: "340 N Broadway, Salem, NH 03079", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Derry", address: "28 Crystal Ave, Derry, NH 03038", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Costco — Nashua", address: "1000 E Hollis St, Nashua, NH 03060", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Derry", address: "2 Leavy Dr, Derry, NH 03038", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Manchester", address: "23 Hazard Ave, Manchester, NH 03109", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Nashua", address: "110 Broad St, Nashua, NH 03064", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Milford", address: "91 Dudley Rd, Milford, NH 03055", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for southern New Hampshire residents.", featured: true },
  ],
  airports: [
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "12 miles from Derry", address: "1 Airport Rd, Manchester, NH 03103", note: "Closest major airport for Derry travelers. International flights to Canada and Europe. Major domestic hub.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: true },
    { code: "BOS", name: "Boston Logan International Airport", distance: "50 miles from Derry", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-93.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
    { code: "PSM", name: "Portsmouth International Airport", distance: "35 miles from Derry", address: "36 New Hampshire Ave, Portsmouth, NH 03801", note: "Regional airport with some international flights. Convenient for Seacoast New Hampshire travelers.", flightUrl: "https://www.flyportsmouth.com/", flightLabel: "Portsmouth Airport", featured: false },
  ],
};